import {Icon, Spinner, SpinnerSize, Tab} from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { ERequestState, ERequestType, HealthTestRequestApi, RequestApi } from "../../../../api";
import {
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  PageBase,
  ProtectedTabs,
  RequestHistoryTab,
  RequestStickyMenu,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { getRouteStr } from "../../../../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../../../../utils/RequestSearchRouteUtil";
import { HealthTestRequestDetail } from "./HealthTestRequestDetail";
import { BillTab } from "../../BillTab";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";

export interface IHealthTestRequestItemPageProps {}

export const HealthTestRequestItemPage: React.FunctionComponent<IHealthTestRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = "0",
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();

  const { t } = useTl();
  const history = useHistory();
  const healthTestRequestApi = useApiService(HealthTestRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    async () =>
      await requestApi.getRequestInfo(
        ERequestType.HealthTestRequest,
        +id,
        +masterRequestId,
        currentLanguage.languageId,
      ),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  //  const api = useApiService(HealthTestRequestApi);

  const closeRequest = React.useCallback(async () => {
    await healthTestRequestApi.close(requestInfo?.parentRequestId);
    refreshRequest();
  }, [healthTestRequestApi, refreshRequest, requestInfo?.parentRequestId]);

  const breadCrumbs = React.useMemo(() => {
    let bc = [
      { text: t(ETLCodes.HealthTestRequests), route: getSearchRouteStr(requestInfo?.requestTypeCode) },
      {
        text: requestInfoLoading ? (
          <Spinner size={SpinnerSize.SMALL} />
        ) : +requestInfo?.requestId === 0 ? (
          t(ETLCodes.New)
        ) : (
          requestInfo.requestNumber
        ),
      },
    ];

    if (requestInfo?.masterRequestId > 0) {
      bc = [
        {
          text: requestInfo.masterRequestDisplayName,
          route: getSearchRouteStr(requestInfo.masterRequestTypeCode),
        },
        {
          text: requestInfo.masterRequestNumber,
          route: getRouteStr(requestInfo.masterRequestTypeCode, requestInfo.masterSubRequestId, "view"),
        },
        ...bc,
      ];
    }
    return bc;
  }, [requestInfo, requestInfoLoading, t]);

  return (
    <PageBase
      breadCrumbs={breadCrumbs}
      actionHistory={
        requestInfo != null && {
          tlCode: ETLCodes.ActionHistoryHealthTestRequest,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0 || requestInfo?.masterRequestId > 0) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.masterRequestId > 0 ? requestInfo.masterRequestId : requestInfo.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsHealthTestRequest"
        onChange={(newTabId) => history.push(`${ERoutes.healthTestRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.healthTestRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={
            <HealthTestRequestDetail healthTestRequestId={+id} editMode={state === "edit"} requestInfo={requestInfo} />
          }
        />
        <Tab id="verification" title={t(ETLCodes.Verifications)} disabled />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <ConditionTab
                requestId={requestInfo?.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.ConditionsForBillingStatus}
                requestState={requestInfo?.requestStateCode}
              />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
